"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SharedCartServiceProvider = void 0;
exports.useSharedCartService = useSharedCartService;
const jsx_runtime_1 = require("react/jsx-runtime");
/* eslint no-console: ["error", { allow: ["error"] }] */
const react_1 = require("react");
const prop_types_1 = __importDefault(require("prop-types"));
const pattern_library_utils_cookies_1 = require("@saatva-bits/pattern-library.utils.cookies");
const actions_1 = require("../services/actions");
const cart_utils_1 = require("../cart.utils");
const globals_1 = require("../globals");
const bundles_1 = require("../utils/bundles");
const initialState = {
    cartId: null,
    cartCount: 0,
    cartSubtotal: 0,
    cart: null,
    isLoading: false,
    removedItem: null,
    addedItems: null,
    isCartFlyoutOpen: false,
    cartIdNotFound: false
};
const genericGraphqlError = 'GraphQL error retrieving cart';
const SharedCartServiceContext = (0, react_1.createContext)(initialState);
const SharedCartServiceProvider = ({ children, apiRoute }) => {
    const [cartId, setCartId] = (0, react_1.useState)(initialState.cartId);
    const [cartCount, setCartCount] = (0, react_1.useState)(initialState.cartCount);
    const [cartSubtotal, setCartSubtotal] = (0, react_1.useState)(initialState.cartSubtotal);
    const [cart, setCart] = (0, react_1.useState)(initialState.cart);
    const [isLoading, setIsLoading] = (0, react_1.useState)(initialState.isLoading);
    const [removedItem, setRemovedItem] = (0, react_1.useState)(initialState.removedItem);
    const [addedItems, setAddedItems] = (0, react_1.useState)(initialState.addedItems);
    const [isCartFlyoutOpen, setIsCartFlyoutOpen] = (0, react_1.useState)(initialState.isCartFlyoutOpen);
    const [cartIdNotFound, setCartIdNotFound] = (0, react_1.useState)(initialState.cartIdNotFound);
    (0, react_1.useEffect)(() => {
        if (cart) {
            setCartCount(Number(cart.totalItems));
            setCartSubtotal(Number(cart.subtotal));
        }
    }, [cart]);
    // Fetch cartId and cartInfo on mount
    (0, react_1.useEffect)(() => {
        const getCartId = (0, cart_utils_1.getCartIdInParams)();
        const cartInfoCookie = pattern_library_utils_cookies_1.Cookies.get('cartInfo');
        if (getCartId) {
            setCartId(getCartId);
        }
        else {
            setCartIdNotFound(true);
        }
        if (cartInfoCookie) {
            const parsedCookie = JSON.parse(cartInfoCookie);
            setCartCount(parsedCookie.quantity);
        }
    }, []);
    // Helper to send analytics events in consuming app, if needed
    const sendAnalyticsEvent = (event, options = {
        cartId: null,
        items: [],
        updateAction: false,
        dimension: null
    }) => {
        switch (event) {
            case 'cartId':
                cart_utils_1.analyticsEvents.pushCartId(options.cartId);
                break;
            case 'view_cart':
                cart_utils_1.analyticsEvents.viewCart(options.items);
                break;
            case 'add_to_cart':
                cart_utils_1.analyticsEvents.pushAddToCart(options.items, options.dimension);
                break;
            case 'move_to_cart':
                options.items.forEach(item => { cart_utils_1.analyticsEvents.pushMoveToCartFromSaveForLater(item); });
                break;
            case 'increase':
            case 'decrease':
                cart.items.forEach(item => { cart_utils_1.analyticsEvents.pushItemUpdate(item, options.updateAction === 'increase'); });
                break;
            case 'extend_add':
            case 'extend_remove':
                options.items.forEach(item => { cart_utils_1.analyticsEvents.pushExtendDataHandler(item, event, options.dimension); });
                break;
            default:
                break;
        }
    };
    const clearCartCookies = () => {
        pattern_library_utils_cookies_1.Cookies.remove('cartId');
        pattern_library_utils_cookies_1.Cookies.remove('cartInfo');
    };
    const createCartIdAndCookie = async (setCookie) => {
        const newCartId = await (0, actions_1.createCartId)(apiRoute);
        if (setCookie) {
            pattern_library_utils_cookies_1.Cookies.set('cartId', newCartId);
        }
        return newCartId;
    };
    const handleCouponCode = async (cartData) => {
        let cartCoupon = cartData.couponCode;
        const couponCookie = pattern_library_utils_cookies_1.Cookies.get('couponCode');
        let updatedCartData = null;
        const applyBeddingCoupon = await (0, cart_utils_1.isBeddingItemsGreaterThan200)(cartData);
        // return early if there's no work to do
        if (!couponCookie) {
            // If the couponCookie is deleted and there is a cartCoupon, remove the coupon
            if (cartCoupon) {
                updatedCartData = await (0, actions_1.removeCoupon)(cartData.cartId, apiRoute);
                cartCoupon = updatedCartData.couponCode;
                return updatedCartData;
            }
            return cartData;
        }
        // if the user has a coupon on cart, but has a different coupon cookie, remove the coupon
        if (cartCoupon && couponCookie && cartCoupon !== couponCookie) {
            updatedCartData = await (0, actions_1.removeCoupon)(cartData.cartId, apiRoute);
            cartCoupon = updatedCartData.couponCode;
        }
        if (cartCoupon === globals_1.couponCodes.checkoutBedding && !applyBeddingCoupon) {
            updatedCartData = await (0, actions_1.removeCoupon)(cartData.cartId, apiRoute);
            cartCoupon = updatedCartData.couponCode;
        }
        // if the user has no cart coupon, but has a coupon cookie add it to cart
        if (!cartCoupon && couponCookie) {
            try {
                if (couponCookie === globals_1.couponCodes.checkoutBedding) {
                    if (applyBeddingCoupon) {
                        updatedCartData = await (0, actions_1.addCoupon)(cartData.cartId, couponCookie, apiRoute);
                    }
                }
                else {
                    updatedCartData = await (0, actions_1.addCoupon)(cartData.cartId, couponCookie, apiRoute);
                }
            }
            catch (error) {
                console.error(error.message);
            } // if a coupon does not current apply to a cart then it will throw an error, but we dont want this method to error
        }
        return updatedCartData || cartData;
    };
    // Cart ACTIONS =====================
    /**
     * CREATE CART: Creates a new cartId and sets the cartId cookie if desired
     * @param {boolean} setCookie default true, sets cartId cookie
     */
    const createCart = async (setCookie = true) => {
        setIsLoading(true);
        try {
            const newCartId = await createCartIdAndCookie(setCookie);
            setCartId(newCartId);
            cart_utils_1.analyticsEvents.pushCartId(newCartId);
            return newCartId;
        }
        catch (error) {
            console.error('Error creating cart:', error.message);
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * GET CART: gets cart data from an existing cartId from a query param, cartId cookie, or a specified one.
     */
    async function getCart() {
        let cartData = null;
        setIsLoading(true);
        try {
            // want to avoid a race here with the state update for the cartId and get it from the cookie itself
            const existingCartId = (0, cart_utils_1.getCartIdInParams)();
            if (!existingCartId) {
                return cartData;
            }
            if (!cartId && existingCartId)
                setCartId(existingCartId);
            if (existingCartId && !cart) {
                const initialCartData = await (0, actions_1.getCartData)(existingCartId, apiRoute);
                if (initialCartData.error || initialCartData.errors) {
                    throw new Error(genericGraphqlError);
                }
                cartData = await handleCouponCode(initialCartData.getCartV2);
                setCart(cartData);
            }
        }
        catch (error) {
            // clearing the cart only when there's a real graphql error and not temp network issues
            if (error.message === genericGraphqlError) {
                setCart(null);
                clearCartCookies();
            }
            console.error('Error fetching cart:', error.message);
        }
        finally {
            setIsLoading(false);
        }
        return cartData;
    }
    const openCartFlyout = (fetchCart = true) => {
        if (isCartFlyoutOpen) {
            return;
        }
        if (fetchCart) {
            getCart();
        }
        setIsCartFlyoutOpen(true);
    };
    const closeCartFlyout = () => {
        setIsCartFlyoutOpen(false);
        setAddedItems(null);
        setRemovedItem(null);
        setIsLoading(true);
    };
    /**
     * ADD TO CART: Adds single or multiple products to an existing or new cart and applies a couponCode if present
     * @param {array} items Cart items to add
     * @param {string} action Corresponds to where and how an item is being added to send the correct analytics event
     * @param {string} location location of where is this action being consumed for analytics events
     */
    const addItemToCart = async (items, action, location, bundleVariant) => {
        let updatedCart = null;
        setIsLoading(true);
        try {
            const newCartId = cartId || await createCart();
            const newCart = await (0, actions_1.addItem)(newCartId, items, apiRoute);
            let dataLayerItems = items;
            if (bundleVariant) {
                dataLayerItems = [...items, bundleVariant];
                (0, bundles_1.persistBundleDataInLocalStorage)(bundleVariant);
            }
            if (newCart) {
                updatedCart = await handleCouponCode(newCart);
                setCart(updatedCart);
                (0, cart_utils_1.calculateCartInfo)(updatedCart);
                // handles cart flyout
                const quantity = (0, cart_utils_1.calculateAddedItemsQuantity)(items);
                setAddedItems({ items, quantity });
                openCartFlyout(false);
                switch (action) {
                    case 'noDataLayerEventPush':
                        break;
                    case 'savedForLater':
                        dataLayerItems.forEach(item => { cart_utils_1.analyticsEvents.pushMoveToCartFromSaveForLater(item); });
                        break;
                    case 'undo':
                        cart_utils_1.analyticsEvents.pushAddToCart(dataLayerItems, location);
                        setRemovedItem(null);
                        break;
                    default:
                        cart_utils_1.analyticsEvents.pushAddToCart(dataLayerItems, location);
                        break;
                }
            }
        }
        catch (error) {
            console.error('Error adding item to cart:', error.message);
            setAddedItems(null);
        }
        finally {
            if (action !== 'configureItem') {
                setIsLoading(false); // skips loading false when configuring items in cart. Loading is set to false in the consuming application.
            }
        }
        return updatedCart;
    };
    /**
     * UPDATE CART: Updates a single item in the cart
     * @param {object} item Cart item object
     * @param {number} quantity quantity of item being updated
     * @param {string} action Corresponds to how where and how an item is being updated to send the correct analytics event
     * @param {string} location location of where is this action being consumed for analytics events
     */
    const updateItemInCart = async (item, quantity, action, location) => {
        let cartToSet = null;
        setIsLoading(true);
        try {
            if (item.warranty) {
                await (0, actions_1.updateItem)(cartId, item.warranty, quantity, apiRoute);
            }
            const newCart = await (0, actions_1.updateItem)(cartId, item, quantity, apiRoute);
            if (newCart) {
                cartToSet = await handleCouponCode(newCart);
                setCart(cartToSet);
                (0, cart_utils_1.calculateCartInfo)(cartToSet);
                cart_utils_1.analyticsEvents.pushItemUpdate(item, action === 'increase', location);
            }
        }
        catch (error) {
            console.error('Error updating item in cart:', error.message);
        }
        finally {
            setIsLoading(false);
        }
        return cartToSet;
    };
    /**
     *REMOVE FROM CART: Removes a product or warranty from existing cart
     * @param {object} item Cart item to remove
     * @param {string} action Corresponds to where and how an item is being removed to send the correct analytics event
     * @param {string} location location of where is this action being consumed for analytics events
     */
    const removeItemFromCart = async (item, action = null, location, triggerBundleEvents = false, bundleVariant) => {
        setIsLoading(true);
        try {
            setAddedItems(null);
            const itemId = item.id;
            const extendId = item.warranty ? item.warranty.id : null;
            let newCart = null;
            switch (action) {
                case 'extend':
                    newCart = await (0, actions_1.removeItem)(cartId, extendId, apiRoute);
                    if (newCart) {
                        cart_utils_1.analyticsEvents.pushExtendDataHandler(item, 'extendRemove', location);
                    }
                    break;
                case 'savedForLater':
                case 'configureItem':
                    if (extendId) {
                        await (0, actions_1.removeItem)(cartId, extendId, apiRoute);
                    }
                    newCart = await (0, actions_1.removeItem)(cartId, itemId, apiRoute);
                    break;
                default:
                    if (extendId) {
                        await (0, actions_1.removeItem)(cartId, extendId, apiRoute);
                    }
                    newCart = await (0, actions_1.removeItem)(cartId, itemId, apiRoute);
                    if (newCart) {
                        setRemovedItem(item);
                        cart_utils_1.analyticsEvents.pushItemUpdate(item, false, location);
                    }
                    break;
            }
            if (triggerBundleEvents && newCart) {
                handleBundleEvents(item, location, newCart, bundleVariant);
            }
            if (newCart) {
                const updatedCart = await handleCouponCode(newCart);
                setCart(updatedCart);
                (0, cart_utils_1.calculateCartInfo)(updatedCart);
            }
        }
        catch (error) {
            console.error('Error updating item in cart:', error);
        }
        finally {
            if (action !== 'configureItem') {
                setIsLoading(false); // skips loading false when configuring items in cart. Loading is set to false in the consuming application.
            }
        }
    };
    /**
     * Handles bundle-related events when an item is removed from the cart.
     *
     * This function checks if the removed item is part of a bundle and triggers
     * appropriate analytics events for bundle breaks. It also verifies if the
     * remaining bundled products are still in the cart and triggers additional
     * events if necessary.
     *
     * @param {object} item - The cart item that was removed.
     * @param {string} location - The location where the action was consumed for analytics events.
     * @param {object} newCart - The updated cart object after the item removal.
     * @param {object} [bundleVariant] - Optional bundle variant data to be used for analytics.
     */
    const handleBundleEvents = (item, location, newCart, bundleVariant) => {
        try {
            const localStorageData = (0, bundles_1.getBundleDataFromLocalStorage)();
            const bundlesAddedToCart = bundleVariant ? [bundleVariant] : (localStorageData || []);
            bundlesAddedToCart.forEach((bundle) => {
                const bundledObjects = bundle.bundledVariants.map(variant => ({ sku: variant.sku, quantity: variant.quantity }));
                // Pillows are storage with pillow sku and quantity of 2, instead of pillow bundle sku.
                // We need to find the pillow sku for the pillow bundle
                const adjustedSku = (0, cart_utils_1.isPillowBundle)(item.sku) ? (0, cart_utils_1.getOriginalPillowBundleSku)(item.sku) : item.sku;
                const isRemovedItemInBundle = bundledObjects.some(bundleObject => bundleObject.sku === adjustedSku);
                const isBundleProduct = globals_1.bundleCodes.includes(bundle.productCode);
                if (isBundleProduct && isRemovedItemInBundle) {
                    cart_utils_1.analyticsEvents.pushBundleBreak(item, location);
                    // check if the non removed bundled products are still in cart
                    const nonRemovedBundledObjects = bundledObjects.filter(bundleObject => bundleObject.sku !== adjustedSku);
                    const isFirstProductToBreakBundle = nonRemovedBundledObjects.every((bundleObject) => {
                        const sku = (0, cart_utils_1.getSkuByBundleKey)(bundleObject.sku);
                        return newCart.items.some(cartItem => cartItem.sku === sku);
                    });
                    if (isFirstProductToBreakBundle) {
                        cart_utils_1.analyticsEvents.pushFirstBundleBreak(item, location);
                    }
                }
            });
        }
        catch (error) {
            console.error(`[removeItemFromCart] Error creating bundle events during cart remove`, error);
        }
    };
    /**
     * ADD WARRANTY TO CART: adds Extend warranty to an eligible product.
     * CANNOT BE TESTED IN BIT
     * @param {object} warrantyDetails {offerPlanId, planId, price, term, title, product}
     * @param {object} item Cart item object
     * @param {string} extendId id of extend plan
     * @param {number} existingQuantity Number of times extend is added for current product
     * @param {string} location location of where is this action being consumed for analytics events
     */
    const addWarrantyToItem = async (warrantyDetails, item, extendId, existingQuantity = null, location) => {
        setIsLoading(true);
        try {
            /*
            When extend lib render the iframe it will pass the item.quantity by value, that
            value wont be updated anymore once the extend instance is created, doesn't really
            care if you re-render the component. Instance will live forever in window object
            with the initial item.quantity value, this is a hack to always get the most recent
            quantity for a cart item.
            */
            const extendSelector = document.getElementById(`${extendId}`);
            const quantityFromSelector = extendSelector ? parseInt(extendSelector.getAttribute('data-quantity'), 10) : existingQuantity;
            const newCart = await (0, actions_1.addWarranty)(cartId, quantityFromSelector, warrantyDetails, apiRoute);
            if (newCart) {
                setCart(newCart);
                setCartId(newCart.cartId);
                (0, cart_utils_1.calculateCartInfo)(newCart);
                cart_utils_1.analyticsEvents.pushExtendDataHandler(item, 'extendAdd', location);
            }
            return newCart;
        }
        catch (error) {
            console.error('Error adding Extend warranty to cart:', error.message);
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * Add a standalone warranty to cart
     * @param {object} warrantyDetails Extend warranty details
     * @param {number} quantity
     * @param {string} leadToken Extend lead token
     * @param {string} manualCartId a specific cartId to add warranty to
     */
    const addWarrantyToCart = async (warrantyDetails, quantity, leadToken = undefined, manualCartId = null) => {
        try {
            setIsLoading(true);
            let newCartId = manualCartId || cartId;
            if (!newCartId && !manualCartId) {
                newCartId = await createCart(apiRoute);
            }
            const newCart = await (0, actions_1.addCustomWarranty)(newCartId, quantity, warrantyDetails, apiRoute, leadToken);
            if (newCart) {
                setCart(newCart);
                setCartId(newCart.cartId);
                (0, cart_utils_1.calculateCartInfo)(newCart);
            }
            return newCart;
        }
        catch (error) {
            console.error('Error adding Extend warranty to cart:', error.message);
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * APPLY COUPON TO CART: applies a specific coupon or any coupon in cookies to cart, overriding any existing applied coupon
     * @param {string} couponCode code to apply
     * @param {string} cartIdOverride cartId to apply if different from existing cartId
     */
    const applyCouponToCart = async (couponCode, cartIdOverride) => {
        setIsLoading(true);
        try {
            const couponToAdd = couponCode || pattern_library_utils_cookies_1.Cookies.get('couponCode');
            const cartIdForCoupon = cartIdOverride || cartId;
            const isCookieAlreadyApplied = couponToAdd === cart.couponCode;
            const newCart = (couponToAdd && !isCookieAlreadyApplied) && await (0, actions_1.addCoupon)(cartIdForCoupon, couponToAdd, apiRoute);
            if (newCart) {
                setCart(newCart);
            }
        }
        catch (error) {
            console.error('Error adding coupon to cart:', error.message);
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * REMOVE COUPON FROM CART: removes a the currently applied coupon from cart
     * @param {string} cartIdOverride cartId to remove coupon from otherwise removes from current cartId
     */
    const removeCouponFromCart = async (cartIdOverride) => {
        setIsLoading(true);
        try {
            const cartIdForCoupon = cartIdOverride || cartId;
            const newCart = cart.couponCode && await (0, actions_1.removeCoupon)(cartIdForCoupon, apiRoute);
            if (newCart) {
                setCart(newCart);
            }
        }
        catch (error) {
            console.error('Error removing coupon from cart:', error.message);
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * UNDO REMOVED ITEM: Re-adds the last removed item to cart
     * @param {string} location location where action was consumed for analytics events
     */
    const undoRemovedItem = async (location) => {
        setIsLoading(true);
        try {
            if (removedItem) {
                await addItemToCart([removedItem], 'undo', location);
            }
        }
        catch {
            console.error('Error undoing removed item:', error.message);
        }
        finally {
            setIsLoading(false);
        }
    };
    return ((0, jsx_runtime_1.jsx)(SharedCartServiceContext.Provider, { value: {
            cartId,
            setCartId,
            cartCount,
            setCartCount,
            cartSubtotal,
            cart,
            setCart,
            setIsLoading,
            isLoading,
            removedItem,
            sendAnalyticsEvent,
            createCart,
            getCart,
            addItemToCart,
            updateItemInCart,
            addWarrantyToItem,
            removeItemFromCart,
            applyCouponToCart,
            removeCouponFromCart,
            undoRemovedItem,
            clearCartCookies,
            createCartIdAndCookie,
            addedItems,
            setAddedItems,
            isCartFlyoutOpen,
            setIsCartFlyoutOpen,
            openCartFlyout,
            closeCartFlyout,
            cartIdNotFound,
            addWarrantyToCart
        }, children: children }));
};
exports.SharedCartServiceProvider = SharedCartServiceProvider;
function useSharedCartService() {
    const sharedCartService = (0, react_1.useContext)(SharedCartServiceContext);
    if (typeof sharedCartService === 'undefined') {
        throw new Error('useSharedCartService must be used within SharedCartServiceProvider');
    }
    return sharedCartService;
}
// add display name for dev tool readability
SharedCartServiceContext.displayName = 'SharedCartService';
SharedCartServiceProvider.propTypes = {
    /** Components that will consume the service */
    children: prop_types_1.default.object,
    /** service-cart-checkout endpoint */
    apiRoute: prop_types_1.default.string
};
//# sourceMappingURL=context.js.map